import { LiquidityAmount, PercentAmount, Token } from '@akropolis-web/primitives';
import { combineLatest, map, Observable } from 'rxjs';

import { protocolsApi } from 'api/modules';
import {
  MarketMetrics,
  FallbackedTotalUSD,
  LendingInfo,
  LendingMetrics,
  ProductSlug,
  VersionSlug,
  NetworkSlug,
} from 'domain/types';
import {
  getMarketTokens,
  hasSignificantAmounts,
  hasOraclePrice,
  hasUnknownPrice,
  calculateUtilizationRatio,
} from 'domain/utils';
import { calcFallbackedTotalUsd } from 'api/utils/calcFallbackedTotalUSD';
import { toEither } from 'utils/either';
import { compareEitherAmounts } from 'utils/amount';

export type MarketStatsPageData = {
  protocolInfo: LendingInfo;
  marketTokens: Token[];
  maxCollateralToken: Token;
  marketData: {
    updatedAt: Date;
    marketStatsData: MarketStatsData[];
    tokens: Token[];
    balance: LendingMetrics['partialBalanceUSD'];
    balanceDayAgo: LendingMetrics['partialBalanceUSD'];
    hasOraclePrice: boolean;
    hasUnknownPrice: boolean;
  } | null;
};

export type MarketStatsData = {
  token: Token;
  utilizationRatio: PercentAmount;
  supplyAPY: PercentAmount | null;
  borrowAPY: PercentAmount | null;
  stableBorrowAPY: PercentAmount | null;
  variableBorrowAPY: PercentAmount | null;
  deposit: ColumnData;
  debt: ColumnData;
  collateral: ColumnData;
  lowSupply: boolean;
};

type ColumnData = {
  amount: FallbackedTotalUSD;
  progress: {
    value: LiquidityAmount;
    total: LiquidityAmount;
  } | null;
};

export function getMarketAssetsPageData({
  productSlug,
  versionSlug,
  network,
  marketId,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  marketId: string;
}): Observable<MarketStatsPageData> {
  return combineLatest([
    protocolsApi.getLendingInfo$(true, { productSlug, versionSlug, network }),
    protocolsApi.getMarketMetrics$(marketId, 'dayAgo', {
      productSlug,
      versionSlug,
      network,
    }),
    protocolsApi.getMarketMetrics$(marketId, 'latest', {
      productSlug,
      versionSlug,
      network,
    }),
  ]).pipe(
    map(([protocolInfo, metricsDayAgo, metrics]) => {
      const tokens = getMarketTokens({ protocolInfo, marketId });
      const maxCollateralToken =
        [...metrics.assetTotals].sort((a, b) =>
          compareEitherAmounts(
            a.balanceUSD.map(balance => balance.totalCollateral),
            b.balanceUSD.map(balance => balance.totalCollateral),
          ),
        )?.[0].token || tokens[0];

      const props: MarketStatsPageData = {
        protocolInfo,
        marketTokens: tokens,
        maxCollateralToken,
        marketData: getMarketData({
          metrics,
          tokens,
          metricsDayAgo,
        }),
      };
      return props;
    }),
  );
}

function getMarketData({
  tokens,
  metrics,
  metricsDayAgo,
}: {
  metrics: MarketMetrics;
  tokens: Token[];
  metricsDayAgo: MarketMetrics;
}): MarketStatsPageData['marketData'] {
  const totalDeposits: FallbackedTotalUSD[] = metrics.assetTotals.map(assetTotal =>
    calcFallbackedTotalUsd([assetTotal], 'totalDeposit'),
  );

  const totalDebts: FallbackedTotalUSD[] = metrics.assetTotals.map(assetTotal =>
    calcFallbackedTotalUsd([assetTotal], 'totalDebt'),
  );

  const totalCollaterals: FallbackedTotalUSD[] = metrics.assetTotals.map(assetTotal =>
    calcFallbackedTotalUsd([assetTotal], 'totalCollateral'),
  );

  return {
    updatedAt: new Date(metrics.timestamp),
    marketStatsData: metrics.assetTotals.map((assetTotal, index) => {
      const data: MarketStatsData = {
        token: assetTotal.token,
        utilizationRatio: calculateUtilizationRatio(assetTotal.balance),
        supplyAPY: assetTotal.supplyAPY,
        borrowAPY: assetTotal.borrowAPY,
        stableBorrowAPY: assetTotal.stableBorrowAPY,
        variableBorrowAPY: assetTotal.variableBorrowAPY,
        deposit: {
          amount: totalDeposits[index],
          progress: toEither(totalDeposits[index].value).fold(
            value => ({
              value,
              total: metrics.partialBalanceUSD.totalDeposit,
            }),
            () => null,
          ),
        },
        debt: {
          amount: totalDebts[index],
          progress: toEither(totalDebts[index].value).fold(
            value => ({
              value,
              total: metrics.partialBalanceUSD.totalDebt,
            }),
            () => null,
          ),
        },
        collateral: {
          amount: totalCollaterals[index],
          progress: toEither(totalCollaterals[index].value).fold(
            value => ({
              value,
              total: metrics.partialBalanceUSD.totalCollateral,
            }),
            () => null,
          ),
        },
        lowSupply: !hasSignificantAmounts(assetTotal.balance),
      };
      return data;
    }),
    balance: metrics.partialBalanceUSD,
    balanceDayAgo: metricsDayAgo?.partialBalanceUSD,
    hasOraclePrice: hasOraclePrice(metrics.assetTotals),
    hasUnknownPrice: hasUnknownPrice(metrics.assetTotals),
    tokens,
  };
}
